import { useMemo } from "react";
import { PawPrint, BadgeCheck } from "lucide-react";
import shareCardBg from "@/assets/share-card-bg.png";

interface Props {
  backgroundDataUrl?: string;
}

const shelterNames = [
  "KTOZ Kraków",
  "Schronisko LPGK Legnica",
  "Koci Azyl Żagań",
  "Schronisko Kielce",
  "Schronisko Piotrków Tryb.",
  "Schronisko Kiczarów",
  "Schronisko Chełm",
  "Schronisko Milanówek",
  "Schronisko w Borku",
  "Schronisko Ciapek",
  "Schronisko Sokółka",
  "Jedno Serce Nie Da Rady",
];

export const InstagramStory12Clean = ({ backgroundDataUrl }: Props) => {
  const bgImage = backgroundDataUrl || shareCardBg;

  const shelterNameLines = useMemo(() => {
    const maxWidth = 900;
    const sep = " • ";

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return [shelterNames];

      ctx.font = "600 34px Nunito";
      const sepW = ctx.measureText(sep).width;

      const lines: string[][] = [];
      let current: string[] = [];
      let currentW = 0;

      for (const name of shelterNames) {
        const w = ctx.measureText(name).width;
        const addW = current.length === 0 ? w : sepW + w;

        if (current.length > 0 && currentW + addW > maxWidth) {
          lines.push(current);
          current = [name];
          currentW = w;
        } else {
          current.push(name);
          currentW += addW;
        }
      }

      if (current.length) lines.push(current);
      return lines.length ? lines : [shelterNames];
    } catch {
      return [shelterNames];
    }
  }, []);

  return (
    <div
      style={{
        width: "1080px",
        height: "1920px",
        fontFamily: "'Nunito', sans-serif",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#1a3a2a",
      }}
    >
      <img
        src={bgImage}
        alt=""
        draggable={false}
        data-export-bg="true"
        loading="eager"
        decoding="sync"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      />

      {/* Verification badge in corner */}
      <div
        style={{
          position: "absolute",
          top: "300px",
          right: "80px",
          background: "linear-gradient(135deg, #5D9A7D 0%, #4E8A6E 100%)",
          borderRadius: "20px",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          zIndex: 3,
        }}
      >
        <BadgeCheck style={{ width: "32px", height: "32px", color: "#FFFFFF" }} />
        <span style={{ fontSize: "24px", fontWeight: 700, color: "#FFFFFF" }}>
          Zweryfikowane
        </span>
      </div>

      {/* Safe zone content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "280px 80px 280px",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Main content - centered */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "-80px",
          }}
        >
          {/* Giant number */}
          <div
            style={{
              fontSize: "300px",
              fontWeight: 800,
              color: "#4E8A6E",
              lineHeight: 0.8,
              textShadow: "0 8px 40px rgba(78,138,110,0.25)",
            }}
          >
            12
          </div>

          {/* Subtitle with confetti emoji */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              marginTop: "8px",
              marginBottom: "48px",
            }}
          >
            <span style={{ fontSize: "40px", fontWeight: 700, color: "#2D5A3D" }}>
              organizacji zweryfikowało już swój profil
            </span>
            <span style={{ fontSize: "48px" }}>🎉</span>
          </div>

          {/* Shelter names as flowing text */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              maxWidth: "900px",
              lineHeight: 1.6,
            }}
          >
            {shelterNameLines.map((line, i) => (
              <div key={i} style={{ whiteSpace: "nowrap" }}>
                <span
                  style={{
                    fontSize: "34px",
                    fontWeight: 600,
                    color: "#4A7A5E",
                  }}
                >
                  {line.join(" • ")}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div
              style={{
                background: "linear-gradient(135deg, #5D9A7D 0%, #4E8A6E 100%)",
                borderRadius: "24px",
                padding: "24px 48px",
                marginBottom: "24px",
              }}
            >
              <p style={{ fontSize: "32px", fontWeight: 700, color: "#FFFFFF", margin: 0 }}>
                Sprawdź ich potrzeby! 💚
              </p>
            </div>

            {/* Logo - aligned to left edge of CTA */}
            <div
              style={{
                background: "#4E8A6E",
                borderRadius: "18px",
                width: "64px",
                height: "64px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PawPrint style={{ width: "36px", height: "36px", color: "#FFFFFF" }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
